import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PlayCircle, Heart, Loader2, Plus, Filter, Eye, X as CloseX, ExternalLink, Facebook, Instagram, Youtube, Twitter, Video as VideoIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

// Platform-aware icon + gradient for the thumbnail fallback so a Facebook /
// Instagram / TikTok card no longer shows a generic letter placeholder.
const PLATFORM_META = {
  youtube:   { Icon: Youtube,    label: "YouTube",   grad: "from-red-700 to-red-900" },
  vimeo:     { Icon: VideoIcon,  label: "Vimeo",     grad: "from-cyan-700 to-cyan-950" },
  facebook:  { Icon: Facebook,   label: "Facebook",  grad: "from-blue-700 to-blue-950" },
  instagram: { Icon: Instagram,  label: "Instagram", grad: "from-pink-600 via-fuchsia-600 to-orange-500" },
  tiktok:    { Icon: VideoIcon,  label: "TikTok",    grad: "from-stone-800 to-black" },
  twitter:   { Icon: Twitter,    label: "X/Twitter", grad: "from-stone-800 to-black" },
  direct:    { Icon: VideoIcon,  label: "Video",     grad: "from-purple-800 to-purple-950" },
  external:  { Icon: ExternalLink, label: "Link",    grad: "from-stone-700 to-stone-900" },
};

/**
 * Educational video feed.
 * - Portrait-first player (Shorts/Reels aesthetic) but supports landscape/square too.
 * - Embeds YouTube/Vimeo via iframe or direct-mp4 via <video>.
 * - Users can add any URL — server sniffs the source and returns an embed URL.
 */
export default function Learn() {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [tags, setTags] = useState([]);
  const [tag, setTag] = useState("");
  const [aspect, setAspect] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [playing, setPlaying] = useState(null);

  const refresh = () => {
    setLoading(true);
    const params = {};
    if (tag) params.tag = tag;
    if (aspect) params.aspect = aspect;
    api.get("/videos", { params }).then(r => setVideos(r.data)).catch(() => setVideos([])).finally(() => setLoading(false));
  };
  useEffect(refresh, [tag, aspect]);
  useEffect(() => { api.get("/videos/meta/tags").then(r => setTags(r.data.tags)).catch(() => {}); }, []);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-6">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-purple-700 bg-purple-50 border border-purple-200 px-3 py-1 rounded-full mb-2">
            <PlayCircle size={12} /> Learn on Find2Near
          </div>
          <h1 className="heading text-4xl md:text-5xl font-extrabold text-stone-900 leading-tight">Educational videos</h1>
          <p className="text-stone-600 mt-1 text-sm">Bite-sized learning from any source — teachers, creators, YouTube channels. Portrait-friendly.</p>
        </div>
        {user && !creating && (
          <Button onClick={() => setCreating(true)} className="rounded-full bg-purple-600 hover:bg-purple-700" data-testid="learn-open-form">
            <Plus size={14} className="mr-1.5" /> Add a video
          </Button>
        )}
      </div>

      {creating && <VideoForm tags={tags} onDone={() => { setCreating(false); refresh(); }} onCancel={() => setCreating(false)} />}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="learn-filters">
        <div className="flex items-center gap-1 text-xs text-stone-500 font-bold"><Filter size={12} /> Filter:</div>
        <Select value={tag} onValueChange={setTag}>
          <SelectTrigger className="h-8 w-32 rounded-full text-xs" data-testid="learn-tag-filter"><SelectValue placeholder="All tags" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tags</SelectItem>
            {tags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={aspect} onValueChange={setAspect}>
          <SelectTrigger className="h-8 w-36 rounded-full text-xs" data-testid="learn-aspect-filter"><SelectValue placeholder="Any aspect" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any aspect</SelectItem>
            <SelectItem value="portrait">Portrait (9:16)</SelectItem>
            <SelectItem value="landscape">Landscape (16:9)</SelectItem>
            <SelectItem value="square">Square (1:1)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="p-12 text-center text-stone-500"><Loader2 size={16} className="inline animate-spin mr-2" /> Loading videos…</div>
      ) : videos.length === 0 ? (
        <div className="p-12 text-center text-stone-500">No videos yet. {user && "Be the first to add one!"}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="learn-grid">
          {videos.map(v => <VideoCard key={v.video_id} v={v} onPlay={() => setPlaying(v)} />)}
        </div>
      )}

      {/* Modal player */}
      {playing && <VideoPlayer video={playing} onClose={() => setPlaying(null)} />}
    </div>
  );
}

function VideoCard({ v, onPlay }) {
  // Aspect-ratio classes: portrait tall, landscape wide, square 1:1.
  const aspectClass = v.aspect_ratio === "portrait" ? "aspect-[9/16]" : v.aspect_ratio === "landscape" ? "aspect-video" : "aspect-square";
  const meta = PLATFORM_META[v.source] || PLATFORM_META.external;
  const [thumbErr, setThumbErr] = useState(false);
  const showThumb = v.thumbnail && !thumbErr;
  return (
    <button
      onClick={onPlay}
      className={`group relative bg-stone-900 rounded-2xl overflow-hidden ${aspectClass} focus:outline-none focus:ring-2 focus:ring-purple-500`}
      data-testid={`learn-card-${v.video_id}`}
    >
      {showThumb ? (
        <img
          loading="lazy"
          src={v.thumbnail}
          alt={v.title}
          onError={() => setThumbErr(true)}
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
        />
      ) : (
        <div className={`w-full h-full bg-gradient-to-br ${meta.grad} grid place-items-center text-white`}>
          <div className="flex flex-col items-center gap-2">
            <meta.Icon size={40} className="opacity-90" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/80">{meta.label}</span>
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent p-3 flex flex-col justify-end text-left">
        <div className="text-white text-xs font-bold line-clamp-2 drop-shadow-lg">{v.title}</div>
        <div className="text-[10px] text-white/70 mt-1 flex items-center gap-2">
          <span className="inline-flex items-center gap-0.5"><Eye size={10} /> {v.view_count || 0}</span>
          <span className="inline-flex items-center gap-0.5"><Heart size={10} /> {v.like_count || 0}</span>
          {v.open_external && <span className="ml-auto inline-flex items-center gap-0.5 text-white/60"><ExternalLink size={10} /></span>}
        </div>
      </div>
      <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
        <PlayCircle size={48} className="text-white drop-shadow-lg" />
      </div>
    </button>
  );
}

function VideoPlayer({ video, onClose }) {
  const aspectClass = video.aspect_ratio === "portrait" ? "aspect-[9/16] max-h-[85vh]" : video.aspect_ratio === "square" ? "aspect-square max-h-[85vh]" : "aspect-video";
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(video.like_count || 0);
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const { user } = useAuth();

  useEffect(() => { api.get(`/videos/${video.video_id}`).catch(() => {}); }, [video.video_id]);

  // If backend flagged the platform as requiring external open (Facebook, X …),
  // don't even attempt the iframe — offer a clean CTA instead.
  const forceExternal = video.open_external === true;

  const toggleLike = async () => {
    if (!user) return toast.error("Sign in to like");
    try {
      const r = await api.post(`/videos/${video.video_id}/like`);
      setLiked(r.data.liked); setLikes(x => x + (r.data.liked ? 1 : -1));
    } catch (_) { /* silent */ }
  };

  const isDirect = video.source === "direct";
  const canIframe = !isDirect && !forceExternal;
  const externalHref = video.external_url || video.url || video.embed_url;
  const meta = PLATFORM_META[video.source] || PLATFORM_META.external;

  return (
    <div className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="learn-player">
      <div onClick={e => e.stopPropagation()} className="relative w-full max-w-md">
        <button onClick={onClose} className="absolute -top-10 right-0 text-white hover:text-purple-400" data-testid="learn-player-close"><CloseX size={24} /></button>
        <div className={`${aspectClass} w-full bg-black rounded-2xl overflow-hidden relative`}>
          {isDirect ? (
            <video src={video.embed_url} controls className="w-full h-full" data-testid="learn-player-native" />
          ) : canIframe && !iframeBlocked ? (
            <iframe
              src={video.embed_url}
              title={video.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onError={() => setIframeBlocked(true)}
              data-testid="learn-player-iframe"
            />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${meta.grad} flex flex-col items-center justify-center text-white p-6 text-center`}>
              <meta.Icon size={56} className="mb-3 opacity-90" />
              <div className="text-lg font-bold mb-1">{meta.label} content</div>
              <div className="text-xs text-white/80 mb-5 max-w-xs">This platform doesn't allow direct embedding here — open the original page to play it.</div>
              <a
                href={externalHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-stone-900 font-bold px-4 py-2 rounded-full hover:bg-stone-100"
                data-testid="learn-player-open-external"
              >
                <ExternalLink size={14} /> Watch on {meta.label}
              </a>
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl mt-3 p-4">
          <div className="font-bold text-stone-900">{video.title}</div>
          {video.description && <div className="text-xs text-stone-600 mt-1">{video.description}</div>}
          <div className="flex items-center gap-3 mt-3 text-xs">
            <button onClick={toggleLike} className={`inline-flex items-center gap-1 font-bold ${liked ? "text-rose-600" : "text-stone-500 hover:text-rose-600"}`} data-testid="learn-player-like">
              <Heart size={14} fill={liked ? "currentColor" : "none"} /> {likes}
            </button>
            <a href={externalHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-bold text-stone-500 hover:text-purple-700" data-testid="learn-player-source">
              <ExternalLink size={12} /> Open source
            </a>
            <span className="text-stone-400">by {video.creator_name}</span>
            {video.tags?.length > 0 && (
              <div className="flex gap-1 flex-wrap ml-auto">
                {video.tags.slice(0, 3).map(t => <span key={t} className="text-[10px] font-bold uppercase bg-stone-100 rounded-full px-2 py-0.5">{t}</span>)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function VideoForm({ tags, onDone, onCancel }) {
  const [f, setF] = useState({ url: "", title: "", description: "", tags: [], language: "", aspect_ratio: "portrait" });
  const [saving, setSaving] = useState(false);

  const toggleTag = (t) => setF(x => ({ ...x, tags: x.tags.includes(t) ? x.tags.filter(y => y !== t) : [...x.tags, t] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^https?:/.test(f.url)) return toast.error("URL must start with http(s)://");
    setSaving(true);
    try {
      await api.post("/videos", f);
      toast.success("Video added");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border-2 border-purple-100 rounded-2xl p-5 mb-5 space-y-3" data-testid="learn-form">
      <div className="text-[11px] uppercase tracking-widest font-bold text-purple-700">Add an educational video</div>
      <Input value={f.url} onChange={e => setF({ ...f, url: e.target.value })} placeholder="YouTube / Vimeo / any video URL" required className="rounded-xl" data-testid="learn-url" />
      <Input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} placeholder="Video title" required className="rounded-xl" data-testid="learn-title" />
      <Textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })} placeholder="What will viewers learn?" rows={2} className="rounded-xl" data-testid="learn-desc" />
      <div className="grid grid-cols-2 gap-3">
        <Select value={f.aspect_ratio} onValueChange={v => setF({ ...f, aspect_ratio: v })}>
          <SelectTrigger className="rounded-xl" data-testid="learn-aspect"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="portrait">Portrait (9:16)</SelectItem>
            <SelectItem value="landscape">Landscape (16:9)</SelectItem>
            <SelectItem value="square">Square (1:1)</SelectItem>
          </SelectContent>
        </Select>
        <Input value={f.language} onChange={e => setF({ ...f, language: e.target.value })} placeholder="Language (e.g. hindi)" className="rounded-xl" data-testid="learn-lang" />
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Tags (pick up to 5)</label>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {tags.map(t => (
            <button
              key={t}
              type="button"
              onClick={() => toggleTag(t)}
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full border-2 capitalize ${f.tags.includes(t) ? "bg-purple-600 text-white border-purple-600" : "bg-white text-stone-700 border-stone-200 hover:border-purple-400"}`}
              data-testid={`learn-tag-${t}`}
            >{t}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-full">Cancel</Button>
        <Button type="submit" disabled={saving} className="rounded-full bg-purple-600 hover:bg-purple-700 ml-auto" data-testid="learn-submit">
          {saving ? <Loader2 size={14} className="animate-spin" /> : "Add video"}
        </Button>
      </div>
    </form>
  );
}
